import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardDocumentListIcon, XMarkIcon } from '@heroicons/react/24/outline';
import AuditActivityTab from './AuditActivityTab';

/**
 * Icon button that opens an audit-history modal scoped to a single record.
 * Drop-in replacement for inline <AuditActivityTab> on detail pages — keeps
 * the page itself uncluttered and only loads history when the user asks.
 *
 *   <AuditHistoryButton module={AUDIT_MODULES.TRIP} recordId={shipment.id} />
 *
 * Variants:
 *   variant="icon"   icon-only round button (default)
 *   variant="button" icon + label, matches inline action buttons
 */
const AuditHistoryButton = ({ module, recordId, label, variant = 'icon', className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const title = label || t('audit.activity_history', 'Activity / History');

  if (!recordId) return null;

  const trigger = variant === 'button' ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 ${className}`}
      title={title}
    >
      <ClipboardDocumentListIcon className="w-4 h-4" />
      <span>{title}</span>
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-blue-600 ${className}`}
      title={title}
      aria-label={title}
    >
      <ClipboardDocumentListIcon className="w-5 h-5" />
    </button>
  );

  return (
    <>
      {trigger}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-black/40" onClick={() => setOpen(false)}>
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600" />
                {title}
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-700"
                aria-label={t('common.close', 'Close')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              <AuditActivityTab module={module} recordId={recordId} hideHeader />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditHistoryButton;
